import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useClientPreset } from '@/contexts/ClientPresetContext'
import { assetsApi, contentApi, api, StoredTitle, StoredHeadline } from '../lib/api'
import VideoAsset from '../components/VideoAsset'
import AICreativeAssistant from '../components/AICreativeAssistant'

interface UploadedAsset {
  file: File
  preview: string
}

interface ExistingAsset {
  id: number
  fbid?: string
  url: string
  thumbnail_url?: string
  type: string
  title?: string
  description?: string
  file_size?: number
  created_at: string
  width?: number
  height?: number
}

interface AdVariant {
  id: number
  headline: string
  body: string
  asset_id: number
  asset_url: string
  asset_thumbnail_url?: string
  asset_type: string
  is_approved: boolean
  created_at: string
}

export default function UploadPage() {
  const [currentStep, setCurrentStep] = useState<'upload' | 'variants' | 'approve'>('upload')
  const [assets, setAssets] = useState<UploadedAsset[]>([])
  const [existingAssets, setExistingAssets] = useState<ExistingAsset[]>([])
  const [selectedExistingAssets, setSelectedExistingAssets] = useState<number[]>([])
  const [titles, setTitles] = useState<string[]>([''])
  const [headlines, setHeadlines] = useState<string[]>([''])
  
  // Stored content state
  const [storedTitles, setStoredTitles] = useState<StoredTitle[]>([])
  const [storedHeadlines, setStoredHeadlines] = useState<StoredHeadline[]>([])
  const [selectedStoredTitles, setSelectedStoredTitles] = useState<number[]>([])
  const [selectedStoredHeadlines, setSelectedStoredHeadlines] = useState<number[]>([])
  const [loadingStoredContent, setLoadingStoredContent] = useState(false)
  const [contentFilter, setContentFilter] = useState<'all' | 'favorites' | 'selected'>('all')
  
  const [uploading, setUploading] = useState(false)
  const [generating, setGenerating] = useState(false)
  // const [approving, setApproving] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [variants, setVariants] = useState<AdVariant[]>([])
  const [selectedVariants, setSelectedVariants] = useState<number[]>([])
  const [variantSetId, setVariantSetId] = useState<number | null>(null)
  const [loadingExisting, setLoadingExisting] = useState(false)
  // const [loadingMeta, setLoadingMeta] = useState(false)
  const [syncingMeta, setSyncingMeta] = useState(false)
  const [reimportingMeta, setReimportingMeta] = useState(false)
  
  // Pagination and filtering state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(21) // Show 21 items per page (changed from 50)
  const [assetFilter, setAssetFilter] = useState<'all' | 'image' | 'video' | 'selected'>('all')
  const [totalAssets, setTotalAssets] = useState(0)
  
  // Type counts for filter labels
  const [imageCount, setImageCount] = useState(0)
  const [videoCount, setVideoCount] = useState(0)
  
  // Sorting state
  const [sortBy, setSortBy] = useState<'created_at' | 'title' | 'type' | 'file_size'>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  
  // File input ref for upload functionality
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const navigate = useNavigate()
  const { selectedPreset, presets, selectPreset } = useClientPreset()

  // Marti AI Assistant callbacks
  const handleContentGenerated = async (content: any) => {
    console.log('AI generated content:', content)
    
    // Auto-populate titles and headlines from AI suggestions
    if (content.titles?.length > 0) {
      const newTitles = content.titles.map((title: any) => title.text)
      setTitles(prev => [...prev.filter(t => t.trim()), ...newTitles])
      toast.success(`Added ${newTitles.length} AI-generated titles!`)
    }
    
    if (content.headlines?.length > 0) {
      const newHeadlines = content.headlines.map((headline: any) => headline.text)
      setHeadlines(prev => [...prev.filter(h => h.trim()), ...newHeadlines])
      toast.success(`Added ${newHeadlines.length} AI-generated headlines!`)
    }

    // Auto-save AI-generated content to the library
    if ((content.titles?.length > 0 || content.headlines?.length > 0) && selectedPreset) {
      try {
        const clientId = getClientId()
        
        const titleRequests = content.titles?.map((title: any) => ({
          text: title.text,
          approach: title.approach || 'AI-generated',
          target_emotion: title.target_emotion,
          client_id: clientId
        })) || []
        
        const headlineRequests = content.headlines?.map((headline: any) => ({
          text: headline.text,
          style: headline.style || 'AI-generated',
          length: headline.length,
          cta_focus: headline.cta_focus,
          client_id: clientId
        })) || []
        
        if (titleRequests.length > 0 || headlineRequests.length > 0) {
          await contentApi.bulkSaveContent({
            titles: titleRequests,
            headlines: headlineRequests
          })
          
          console.log('Auto-saved AI-generated content to library')
          
          // Refresh stored content to show new items
          fetchStoredContent()
        }
      } catch (error) {
        console.error('Failed to auto-save AI content:', error)
        // Don't show error toast here since the content was still added to the form
      }
    }
  }

  const handleImageGenerated = (generatedAssets: any[]) => {
    console.log('AI generated images:', generatedAssets)
    
    // Refresh the existing assets to include newly generated images
    fetchExistingAssets(currentPage, assetFilter)
    toast.success(`Generated ${generatedAssets.length} AI images! They've been added to your asset library.`)
  }

  // Helper function to extract database client ID from preset
  const getClientId = (): number => {
    if (!selectedPreset) return 4; // fallback
    
    // Check if it's a database client (format: "db-client-{id}")
    if (selectedPreset.id.startsWith('db-client-')) {
      const clientId = parseInt(selectedPreset.id.replace('db-client-', ''));
      return clientId || 4; // fallback to 4 if parsing fails
    }
    
    // For local presets, use default client ID 4
    return 4;
  };

  // Reset state when preset changes
  useEffect(() => {
    setCurrentStep('upload')
    setAssets([])
    setTitles([''])
    setHeadlines([''])
    setVariants([])
    setSelectedExistingAssets([])
    setExistingAssets([])
    setSelectedStoredTitles([])
    setSelectedStoredHeadlines([])
    setStoredTitles([])
    setStoredHeadlines([])
    
    // Load existing assets and content for the new preset (database only)
    if (selectedPreset) {
      console.log('Selected preset changed to:', selectedPreset)
      fetchExistingAssets()
      fetchStoredContent()
    }
  }, [selectedPreset])

  // Refetch assets when sorting changes
  useEffect(() => {
    if (selectedPreset && assetFilter !== 'selected') {
      fetchExistingAssets(currentPage, assetFilter)
    }
  }, [sortBy, sortOrder])

  const fetchSelectedAssets = async () => {
    if (!selectedPreset || selectedExistingAssets.length === 0) {
      setExistingAssets([])
      setTotalAssets(0)
      return
    }
    
    setLoadingExisting(true)
    try {
      const clientId = getClientId();
      
      console.log(`Fetching selected assets for client ${clientId}:`, selectedExistingAssets)
      
      // Fetch selected assets by their IDs
      const response = await assetsApi.getAssetsByIds(clientId, selectedExistingAssets)
      console.log('Selected assets response:', response.data)
      
      const selectedAssets = response.data.assets || []
      
      setExistingAssets(selectedAssets)
      setTotalAssets(selectedAssets.length)
      
    } catch (error: any) {
      console.error('Error fetching selected assets:', error)
      setExistingAssets([])
      setTotalAssets(0)
      toast.error('Failed to fetch selected assets')
    } finally {
      setLoadingExisting(false)
    }
  }

  const fetchExistingAssets = async (page: number = 1, filter: 'all' | 'image' | 'video' | 'selected' = 'all') => {
    if (!selectedPreset) return
    
    setLoadingExisting(true)
    try {
      const clientId = getClientId();
      
      console.log(`Fetching existing assets for client ${clientId} from database (page ${page}, filter: ${filter})...`)
      
      // Calculate offset for pagination
      const offset = (page - 1) * itemsPerPage
      
      // Fetch more assets to account for filtering
      const limit = filter === 'all' ? itemsPerPage : itemsPerPage * 3 // Fetch more when filtering
      
      // Pass type filter to backend API
      const typeFilter = filter !== 'all' && filter !== 'selected' ? filter : undefined
      const response = await assetsApi.getUserAssets(clientId, limit, offset, undefined, sortBy, sortOrder, typeFilter)
      console.log('Existing assets response:', response.data)
      console.log('Response structure:', {
        assets: Array.isArray(response.data.assets),
        length: response.data?.assets?.length || 0,
        firstAsset: response.data?.assets?.[0],
        totalCount: response.data?.total_count
      })
      
      // Extract assets array and pagination info from API response
      const allAssets = Array.isArray(response.data.assets) ? response.data.assets : []
      const totalCount = response.data.total_count || 0
      const apiImageCount = response.data.image_count || 0
      const apiVideoCount = response.data.video_count || 0
      
      console.log('All assets after processing:', allAssets.length, 'assets')
      
      // Update type counts from API
      setImageCount(apiImageCount)
      setVideoCount(apiVideoCount)
      
      // Apply client-side filtering only for 'selected' filter (backend handles type filtering)
      let filteredAssets = allAssets
      if (filter === 'selected') {
        filteredAssets = allAssets.filter((asset: any) => selectedExistingAssets.includes(asset.id))
        setTotalAssets(filteredAssets.length) // For selected, use filtered count
      } else {
        // Backend already filtered by type, use all returned assets
        setTotalAssets(totalCount) // Use total count from API
      }
      
      console.log('Filtered assets:', {
        filter,
        originalCount: allAssets.length,
        filteredCount: filteredAssets.length
      })
      
      setExistingAssets(filteredAssets)
      
      if (filteredAssets.length === 0) {
        console.warn('No assets found after filtering. This might indicate:')
        console.warn('1. No assets exist for this client')
        console.warn('2. API returned empty response')
        console.warn('3. Filter is too restrictive')
      }
      
    } catch (error: any) {
      console.error('Error fetching existing assets:', error)
      
      // Handle errors gracefully - don't break the UI
      if (error.response?.status === 404) {
        console.log('No existing assets found')
        setExistingAssets([])
        setTotalAssets(0)
      } else {
        console.error('Failed to fetch existing assets:', error.message)
        setExistingAssets([])
        setTotalAssets(0)
        toast.error('Failed to fetch existing assets')
      }
    } finally {
      setLoadingExisting(false)
    }
  }

  const fetchStoredContent = async () => {
    if (!selectedPreset) return
    
    setLoadingStoredContent(true)
    try {
      const clientId = getClientId();
      console.log(`Fetching stored content for client ${clientId}...`)
      
      const favoritesOnly = contentFilter === 'favorites'
      
      const [titlesResponse, headlinesResponse] = await Promise.all([
        contentApi.getTitles(clientId, favoritesOnly),
        contentApi.getHeadlines(clientId, favoritesOnly)
      ])
      
      setStoredTitles(titlesResponse.data || [])
      setStoredHeadlines(headlinesResponse.data || [])
      
      console.log('Stored content loaded:', {
        titles: titlesResponse.data?.length || 0,
        headlines: headlinesResponse.data?.length || 0
      })
      
    } catch (error: any) {
      console.error('Error fetching stored content:', error)
      
      // Handle errors gracefully
      if (error.response?.status === 404) {
        console.log('No stored content found')
        setStoredTitles([])
        setStoredHeadlines([])
      } else {
        console.error('Failed to fetch stored content:', error.message)
        setStoredTitles([])
        setStoredHeadlines([])
        toast.error('Failed to fetch stored content')
      }
    } finally {
      setLoadingStoredContent(false)
    }
  }

  // Helper function to get filtered assets for display
  const getFilteredAssets = () => {
    // Ensure existingAssets is always an array
    const assets = Array.isArray(existingAssets) ? existingAssets : []
    let filteredAssets = assets
    
    if (assetFilter === 'selected') {
      // Show only selected assets
      filteredAssets = assets.filter((asset: any) => selectedExistingAssets.includes(asset.id))
    } else if (assetFilter !== 'all') {
      // Filter by type (image/video)
      filteredAssets = assets.filter((asset: any) => asset.type === assetFilter)
    }
    
    return filteredAssets
  }

  // Helper function to get pagination info
  const getPaginationInfo = () => {
    const filteredAssets = getFilteredAssets()
    const totalPages = Math.ceil(totalAssets / itemsPerPage)
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = Math.min(startIndex + itemsPerPage, totalAssets)
    
    return {
      totalPages,
      currentAssets: Array.isArray(filteredAssets) ? filteredAssets : [], // Ensure it's always an array
      totalItems: totalAssets,
      startItem: startIndex + 1,
      endItem: endIndex
    }
  }

  // Handler functions for pagination and filtering
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    // Selected filter shows all assets, no pagination needed
    if (assetFilter !== 'selected') {
      fetchExistingAssets(page, assetFilter)
    }
  }

  const handleFilterChange = (filter: 'all' | 'image' | 'video' | 'selected') => {
    setAssetFilter(filter)
    setCurrentPage(1) // Reset to first page when filter changes
    
    if (filter === 'selected') {
      // For selected filter, fetch all selected assets regardless of type
      fetchSelectedAssets()
    } else {
      // For other filters, use normal pagination
      fetchExistingAssets(1, filter)
    }
  }

  const handleRefreshAssets = () => {
    if (assetFilter === 'selected') {
      fetchSelectedAssets()
    } else {
      fetchExistingAssets(currentPage, assetFilter)
    }
  }

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0 || !selectedPreset) return

    setUploading(true)
    setUploadProgress(0)

    try {
      const clientId = getClientId()
      
      // Prepare form data
      const formData = new FormData()
      formData.append('client_id', clientId.toString())
      
      // Add files
      Array.from(files).forEach(file => {
        formData.append('files', file)
      })
      
      // Add default titles and headlines
      Array.from(files).forEach((file, index) => {
        formData.append('titles', file.name.split('.')[0])
        formData.append('headlines', `Uploaded asset ${index + 1}`)
      })
      
      // Upload with progress tracking
      const response = await api.post('/assets/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setUploadProgress(progress)
          }
        }
      })
      
      if (response.data.uploaded_assets?.length > 0) {
        toast.success(`Successfully uploaded ${response.data.uploaded_assets.length} assets!`)
        
        // Refresh the assets list
        await fetchExistingAssets(currentPage, assetFilter)
      } else {
        toast.error('No assets were uploaded')
      }
      
    } catch (error: any) {
      console.error('Upload failed:', error)
      toast.error(error.response?.data?.detail || 'Upload failed')
    } finally {
      setUploading(false)
      setUploadProgress(0)
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const syncMetaAssets = async () => {
    if (!selectedPreset) return
    
    setSyncingMeta(true)
    try {
      const clientId = getClientId();
      
      console.log('Syncing Meta assets in batches...')
      
      let offset = 0
      const batchSize = 10
      let totalSynced = 0
      let hasMore = true
      
      while (hasMore) {
        console.log(`Syncing batch: offset=${offset}, batch_size=${batchSize}`)
        
        const response = await api.get(`/assets/meta/sync/${clientId}`, {
          params: {
            batch_size: batchSize,
            offset: offset
          }
        })
        
        const batchSynced = response.data.synced_count || 0
        totalSynced += batchSynced
        hasMore = response.data.has_more || false
        offset = response.data.next_offset || (offset + batchSize)
        
        console.log(`Batch complete: ${batchSynced} synced, ${totalSynced} total, has_more: ${hasMore}`)
        
        // Small delay to avoid overwhelming the API
        if (hasMore) {
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      }
      
      // If any assets were synced, refresh the existing assets list
      if (totalSynced > 0) {
        console.log(`Synced ${totalSynced} new assets from Meta, refreshing list...`)
        await fetchExistingAssets() // Refresh from database
        toast.success(`Successfully synced ${totalSynced} new assets from Meta!`)
      } else {
        toast.success('No new assets found to sync from Meta')
      }
      
    } catch (error: any) {
      console.error('Meta sync failed:', error)
      toast.error(error.response?.data?.detail || 'Failed to sync Meta assets')
    } finally {
      setSyncingMeta(false)
    }
  }

  const refreshMetaAssets = async () => {
    if (!selectedPreset) return
    
    setReimportingMeta(true)
    try {
      const clientId = getClientId();
      
      console.log('Re-importing Meta assets with fresh URL signatures...')
      
      // Call the correct reimport endpoint to delete and re-import all assets
      const response = await assetsApi.reimportMetaAssets(clientId)
      
      console.log('Meta assets re-import response:', response.data)
      
      // Refresh the existing assets list to get the new assets
      await fetchExistingAssets(currentPage, assetFilter)
      
      const deletedCount = response.data.deleted_count || 0
      const importedCount = response.data.imported_count || 0
      const failedCount = response.data.failed_count || 0
      
      toast.success(`Successfully re-imported ${importedCount} assets! (${deletedCount} old assets deleted, ${failedCount} failed imports)`)
      
    } catch (error: any) {
      console.error('Meta assets re-import failed:', error)
      toast.error(error.response?.data?.detail || 'Failed to re-import Meta assets')
    } finally {
      setReimportingMeta(false)
    }
  }

  const toggleExistingAsset = (assetId: number) => {
    setSelectedExistingAssets(prev => {
      const newSelection = prev.includes(assetId) 
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
      
      // If we're currently viewing the "selected" filter, refresh the view
      if (assetFilter === 'selected') {
        // Use setTimeout to ensure state is updated first
        setTimeout(() => {
          if (newSelection.length > 0) {
            fetchSelectedAssets()
          } else {
            setExistingAssets([])
            setTotalAssets(0)
          }
        }, 0)
      }
      
      return newSelection
    })
  }

  const removeAsset = (index: number) => {
    setAssets(prev => {
      const newAssets = prev.filter((_, i) => i !== index)
      // Clean up object URL
      URL.revokeObjectURL(prev[index].preview)
      return newAssets
    })
  }

  const addTitle = () => {
    setTitles(prev => [...prev, ''])
  }

  const updateTitle = (index: number, value: string) => {
    setTitles(prev => prev.map((title, i) => i === index ? value : title))
  }

  const removeTitle = (index: number) => {
    if (titles.length > 1) {
      setTitles(prev => prev.filter((_, i) => i !== index))
    }
  }

  const addHeadline = () => {
    setHeadlines(prev => [...prev, ''])
  }

  const updateHeadline = (index: number, value: string) => {
    setHeadlines(prev => prev.map((headline, i) => i === index ? value : headline))
  }

  const removeHeadline = (index: number) => {
    if (headlines.length > 1) {
      setHeadlines(prev => prev.filter((_, i) => i !== index))
    }
  }

  const handleGenerateVariants = async () => {
    if (!selectedPreset) {
      toast.error('Please select a client preset first')
      return
    }

    // Validate inputs
    const validTitles = titles.filter(title => title.trim() !== '')
    const validHeadlines = headlines.filter(headline => headline.trim() !== '')

    if (validTitles.length === 0) {
      toast.error('Please add at least one title')
      return
    }

    if (validHeadlines.length === 0) {
      toast.error('Please add at least one headline')
      return
    }

    try {
      setUploading(true)
      setUploadProgress(0)
      
      // Collect all asset IDs (existing + newly uploaded)
      let allAssetIds = [...selectedExistingAssets]
      
      // Upload new assets if any
      if (assets.length > 0) {
        const formData = new FormData()
        assets.forEach((asset) => {
          formData.append(`files`, asset.file)
        })
        
        getClientId(); // Ensure client is selected
        
        // Add simple progress tracking
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => Math.min(prev + 10, 90))
        }, 200)

        console.log('Starting upload with new assets:', assets.length)
        const uploadResponse = await assetsApi.upload(formData)
        
        clearInterval(progressInterval)
        setUploadProgress(100)
        
        console.log('Upload response:', uploadResponse.data)
        
        // Add newly uploaded asset IDs to our list
        allAssetIds.push(...uploadResponse.data.assets.map((asset: any) => asset.id))
      }
      
      // Now generate variants with all asset IDs (existing + newly uploaded)
      setUploading(false)
      setGenerating(true)
      
      const variantRequest = {
        asset_ids: allAssetIds,
        titles: validTitles,
        headlines: validHeadlines,
        client_id: getClientId()
      }
      
      console.log('Generating variants with request:', variantRequest)
      const variantResponse = await assetsApi.generateVariants(variantRequest)
      
      console.log('Variants response:', variantResponse.data)
      
      // Check if we got a valid variant_set_id before starting polling
      if (!variantResponse.data.id) {
        console.error('No variant set id received in response:', variantResponse.data)
        toast.error('Failed to generate variants: Invalid response from server')
        setGenerating(false)
        return
      }
      
      // Start polling for variants
      const pollInterval = setInterval(async () => {
        try {
          const response = await assetsApi.getVariantSet(variantResponse.data.id)
          if (response.data.variants.length > 0) {
            clearInterval(pollInterval)
            setVariants(response.data.variants)
            setVariantSetId(response.data.id)
            setGenerating(false)
            setCurrentStep('variants')
            toast.success(`Generated ${response.data.total_variants} ad variants!`)
          }
        } catch (error) {
          console.error('Error polling for variants:', error)
          clearInterval(pollInterval)
          setGenerating(false)
          toast.error('Failed to retrieve generated variants')
        }
      }, 2000) // Poll every 2 seconds
      
      // Set a timeout to stop polling after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval)
        if (generating) {
          toast.error('Variant generation is taking longer than expected. Please check back later.')
          setGenerating(false)
        }
      }, 5 * 60 * 1000)
      
    } catch (error: any) {
      console.error('Generation error:', error)
      
      let errorMessage = 'Failed to generate variants'
      
      if (error.response) {
        console.log('Server response status:', error.response.status)
        console.log('Server response data:', error.response.data)
        
        if (error.response.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.'
          navigate('/login')
        } else if (error.response.status === 413) {
          errorMessage = 'Files too large. Please reduce file sizes.'
        } else if (error.response.status === 422) {
          // Handle validation errors better
          const details = error.response.data?.detail
          if (Array.isArray(details)) {
            errorMessage = details.map((d: any) => d.msg || d).join(', ')
          } else {
            errorMessage = details || 'Validation error. Please check your request.'
          }
        } else if (error.response.status === 400) {
          errorMessage = error.response.data?.detail || 'Invalid request. Please check your files.'
        } else {
          errorMessage = error.response.data?.detail || `Generation failed with status ${error.response.status}`
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.'
      } else {
        errorMessage = `Request error: ${error.message}`
      }
      
      toast.error(errorMessage)
      setUploading(false)
      setGenerating(false)
      setUploadProgress(0)
    }
  }

  const handleVariantSelect = (variantId: number) => {
    setSelectedVariants(prev => 
      prev.includes(variantId)
        ? prev.filter(id => id !== variantId)
        : [...prev, variantId]
    )
  }

  const handleApproveVariants = async () => {
    if (selectedVariants.length === 0) {
      toast.error('Please select at least one variant')
      return
    }

    if (!variantSetId) {
      toast.error('No variant set found')
      return
    }

    try {
      await assetsApi.approveVariants(variantSetId.toString(), selectedVariants)
      toast.success(`✅ Approved ${selectedVariants.length} variants! You can now use them in your campaigns.`)
      
      // Reset the form to allow creating more variations
      setAssets([])
      setSelectedExistingAssets([])
      setTitles([''])
      setHeadlines([''])
      setVariants([])
      setSelectedVariants([])
      setVariantSetId(null)
      setCurrentStep('upload')
      
      // Refresh existing assets to include any newly uploaded ones
      fetchExistingAssets()
    } catch (error: any) {
      console.error('Approval error:', error)
      toast.error('Failed to approve variants')
    }
  }

  const calculateTotalCombinations = () => {
    const totalAssets = selectedExistingAssets.length + assets.length
    const validTitles = titles.filter(title => title.trim()).length
    const validHeadlines = headlines.filter(headline => headline.trim()).length
    return totalAssets * validTitles * validHeadlines
  }

  const testConnectivity = async () => {
    try {
      console.log('Testing API connectivity...')
      const authResponse = await api.get('/auth/me')
      console.log('Auth test successful:', authResponse.data)
      toast.success('✅ Authentication working')
      
      const assetsResponse = await api.get('/assets')
      console.log('Assets endpoint test successful:', assetsResponse.data)
      toast.success('✅ Assets endpoint working')
      
    } catch (error: any) {
      console.error('Connectivity test failed:', error)
      if (error.response) {
        console.error('Response status:', error.response.status)
        console.error('Response data:', error.response.data)
        toast.error(`❌ API Error: ${error.response.status} - ${error.response.data?.detail || error.response.statusText}`)
      } else if (error.request) {
        toast.error('❌ Network Error: No response from server')
      } else {
        toast.error(`❌ Request Error: ${error.message}`)
      }
    }
  }

  // Toggle stored title selection
  const toggleStoredTitle = (titleId: number) => {
    setSelectedStoredTitles(prev => 
      prev.includes(titleId) 
        ? prev.filter(id => id !== titleId)
        : [...prev, titleId]
    )
  }

  // Toggle stored headline selection
  const toggleStoredHeadline = (headlineId: number) => {
    setSelectedStoredHeadlines(prev => 
      prev.includes(headlineId) 
        ? prev.filter(id => id !== headlineId)
        : [...prev, headlineId]
    )
  }

  // Add selected stored titles to current titles
  const addSelectedTitlesToCurrent = async () => {
    const selectedTitleObjs = storedTitles.filter(title => selectedStoredTitles.includes(title.id))
    const newTitles = selectedTitleObjs.map(title => title.text)
    
    if (newTitles.length > 0) {
      setTitles(prev => [...prev.filter(t => t.trim()), ...newTitles])
      
      // Increment usage count for selected titles
      try {
        await Promise.all(selectedTitleObjs.map(title => contentApi.incrementTitleUsage(title.id)))
      } catch (error) {
        console.error('Failed to increment title usage:', error)
      }
      
      toast.success(`Added ${newTitles.length} stored titles!`)
      setSelectedStoredTitles([])
    }
  }

  // Add selected stored headlines to current headlines
  const addSelectedHeadlinesToCurrent = async () => {
    const selectedHeadlineObjs = storedHeadlines.filter(headline => selectedStoredHeadlines.includes(headline.id))
    const newHeadlines = selectedHeadlineObjs.map(headline => headline.text)
    
    if (newHeadlines.length > 0) {
      setHeadlines(prev => [...prev.filter(h => h.trim()), ...newHeadlines])
      
      // Increment usage count for selected headlines
      try {
        await Promise.all(selectedHeadlineObjs.map(headline => contentApi.incrementHeadlineUsage(headline.id)))
      } catch (error) {
        console.error('Failed to increment headline usage:', error)
      }
      
      toast.success(`Added ${newHeadlines.length} stored headlines!`)
      setSelectedStoredHeadlines([])
    }
  }

  // Save current titles and headlines to storage
  const saveCurrentContent = async () => {
    const clientId = getClientId()
    const validTitles = titles.filter(t => t.trim())
    const validHeadlines = headlines.filter(h => h.trim())
    
    if (validTitles.length === 0 && validHeadlines.length === 0) {
      toast.error('No content to save')
      return
    }
    
    try {
      const titleRequests = validTitles.map(text => ({ text, client_id: clientId }))
      const headlineRequests = validHeadlines.map(text => ({ text, client_id: clientId }))
      
      await contentApi.bulkSaveContent({
        titles: titleRequests,
        headlines: headlineRequests
      })
      
      toast.success(`Saved ${validTitles.length} titles and ${validHeadlines.length} headlines!`)
      
      // Refresh stored content
      fetchStoredContent()
      
    } catch (error: any) {
      console.error('Failed to save content:', error)
      toast.error('Failed to save content')
    }
  }

  // Handle content filter change
  const handleContentFilterChange = (filter: 'all' | 'favorites' | 'selected') => {
    setContentFilter(filter)
    if (filter !== 'selected') {
      fetchStoredContent()
    }
  }

  const refreshVideoStatus = async (assetId: number) => {
    try {
      const response = await fetch(`/api/assets/video/${assetId}/refresh-status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Video status refresh result:', result);
        
        // Refresh the assets list to show updated status
        await fetchExistingAssets(currentPage, assetFilter);
        
        // Show success message
        if (result.is_ready) {
          alert('Video processing complete! Thumbnail should now be visible.');
        } else {
          alert(`Video is still processing. Status: ${result.status}`);
        }
      } else {
        throw new Error('Failed to refresh video status');
      }
    } catch (error) {
      console.error('Error refreshing video status:', error);
      alert('Failed to refresh video status. Please try again.');
    }
  };


  if (currentStep === 'variants') {
    return (
      <div style={{
        width: '100%',
        padding: 'var(--spacing-8) 0',
        fontFamily: 'var(--font-family-appleSystem)',
      }}>
        <div style={{
          background: 'var(--color-card)',
          borderRadius: '24px',
          boxShadow: 'var(--shadow-appleModal)',
          border: '1px solid var(--color-border)',
          padding: 'var(--spacing-8)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-6)',
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-4)' }}>
              <button
                onClick={() => setCurrentStep('upload')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-2)',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  background: 'var(--color-systemBackground)',
                  color: 'var(--color-label)',
                  fontWeight: 500,
                  fontSize: 'var(--font-size-sm)',
                  border: '1px solid var(--color-border)',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
              >
                <svg style={{ width: 16, height: 16 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Upload
              </button>
            </div>
            <h2 style={{
              fontSize: 'var(--font-size-2xl)',
              fontWeight: 700,
              color: 'var(--color-label)',
              marginBottom: 'var(--spacing-2)',
              letterSpacing: 'var(--letter-spacing-tight)',
            }}>
              Generated Ad Variants ({variants.length})
            </h2>
            <p style={{
              color: 'var(--color-secondaryLabel)',
              fontSize: 'var(--font-size-base)',
              fontWeight: 500,
              marginBottom: 'var(--spacing-6)',
            }}>
              Select the variants you want to use for your campaign
            </p>
          </div>

          {/* Variants Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 'var(--spacing-4)',
            maxHeight: '60vh',
            overflowY: 'auto',
            padding: 'var(--spacing-2)',
          }}>
            {variants.map((variant) => (
              <div
                key={variant.id}
                onClick={() => handleVariantSelect(variant.id)}
                style={{
                  position: 'relative',
                  background: 'var(--color-systemBackground)',
                  borderRadius: '16px',
                  border: `2px solid ${selectedVariants.includes(variant.id) ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  padding: 'var(--spacing-4)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  transform: selectedVariants.includes(variant.id) ? 'scale(1.02)' : 'scale(1)',
                }}
              >
                {/* Selection Indicator */}
                {selectedVariants.includes(variant.id) && (
                  <div style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    width: 24,
                    height: 24,
                    background: 'var(--color-primary)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}>
                    ✓
                  </div>
                )}

                {/* Content - Title first (like Meta ad) */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--spacing-3)',
                }}>
                  <h3 style={{
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 600,
                    color: 'var(--color-label)',
                    lineHeight: 1.4,
                    margin: 0,
                    padding: 'var(--spacing-2)',
                    textAlign: 'center',
                  }}>
                    {variant.headline}
                  </h3>

                  {/* Asset Preview - Middle (like Meta ad) */}
                  <div style={{
                    width: '100%',
                    height: '100%',
                    background: 'var(--color-systemGroupedBackground)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid var(--color-border)',
                    overflow: 'hidden',
                    position: 'relative',
                  }}>
                    {variant.asset_thumbnail_url || variant.asset_url ? (
                      <>
                        {variant.asset_type === 'video' ? (
                          <VideoAsset 
                            src={variant.asset_url}
                            assetId={variant.asset_id}
                            poster={variant.asset_thumbnail_url}
                            showPlayButton={true}
                            autoPlayOnHover={true}
                            style={{
                              borderRadius: '8px',
                              width: '100%',
                              height: '100%',
                            }}
                          />
                        ) : (
                          <img
                            src={variant.asset_thumbnail_url || variant.asset_url}
                            alt={`Asset ${variant.asset_id}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                              if (fallback) {
                                fallback.style.display = 'flex';
                              }
                            }}
                          />
                        )}
                      </>
                    ) : (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '2px',
                        color: 'var(--color-tertiaryLabel)',
                      }}>
                        <span style={{ fontSize: '16px' }}>
                          {variant.asset_type === 'video' ? '🎥' : '🖼️'}
                        </span>
                        <span style={{ fontSize: '8px', fontWeight: 600 }}>
                          {variant.asset_type.toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Description - Bottom (like Meta ad) */}
                  <p style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-secondaryLabel)',
                    lineHeight: 1.4,
                    margin: 0,
                    padding: '0 var(--spacing-2) var(--spacing-2)',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: 'vertical',
                    textAlign: 'left',
                  }}>
                    {variant.body}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-3)',
            paddingTop: 'var(--spacing-4)',
            borderTop: '1px solid var(--color-border)',
          }}>
            <div style={{
              color: 'var(--color-secondaryLabel)',
              fontSize: 'var(--font-size-sm)',
            }}>
              {selectedVariants.length} of {variants.length} variants selected
            </div>
            
            <div style={{
              padding: 'var(--spacing-3)',
              background: 'var(--color-primaryTranslucent)',
              borderRadius: '12px',
              border: '1px solid var(--color-primaryBorder)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-label)',
            }}>
              💡 <strong>Tip:</strong> Approve variants to save them for later use in campaigns. You can then use the Advanced Campaign builder to create single or multiple campaigns with these approved variants.
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center'
            }}>
              <button
                onClick={handleApproveVariants}
                disabled={selectedVariants.length === 0}
                style={{
                  background: selectedVariants.length > 0 ? 'var(--color-primary)' : 'var(--color-systemGray4)',
                  color: selectedVariants.length > 0 ? 'white' : 'var(--color-tertiaryLabel)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '12px 32px',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 600,
                  cursor: selectedVariants.length > 0 ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--spacing-2)'
                }}
              >
                ✅ Approve {selectedVariants.length} Variants for Later Use
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      width: '100%',
      padding: 'var(--spacing-8) 0',
      fontFamily: 'var(--font-family-appleSystem)',
    }}>
      {/* Client Selection Header */}
      {!selectedPreset && (
        <div style={{
          background: 'var(--color-card)',
          border: '1px solid var(--color-border)',
          borderRadius: '16px',
          padding: 'var(--spacing-6)',
          marginBottom: 'var(--spacing-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ 
              margin: 0, 
              marginBottom: 'var(--spacing-1)',
              color: 'var(--color-label)',
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semibold)'
            }}>
              Select a Client
            </h2>
            <p style={{ 
              margin: 0, 
              color: 'var(--color-secondaryLabel)',
              fontSize: 'var(--font-size-base)'
            }}>
              Choose a client preset to start creating ad campaigns
            </p>
          </div>
          <select
            value=""
            onChange={(e) => {
              const clientId = e.target.value
              if (clientId) {
                selectPreset(clientId) // selectPreset expects string ID
              }
            }}
            style={{
              width: '200px',
              padding: 'var(--spacing-3)',
              borderRadius: '12px',
              border: '1px solid var(--color-border)',
              background: 'var(--color-card)',
              fontSize: 'var(--font-size-base)',
              color: 'var(--color-label)',
              fontFamily: 'var(--font-family-appleSystem)'
            }}
          >
            <option value="">Select Client</option>
            {presets.map(preset => (
              <option key={preset.id} value={preset.id}>
                {preset.brand} ({preset.industry})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Main Upload Card */}
      <div style={{
        background: 'var(--color-card)',
        borderRadius: '24px',
        boxShadow: 'var(--shadow-appleModal)',
        border: '1px solid var(--color-border)',
        padding: 'var(--spacing-8)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-6)',
      }}>
        {/* Assets Section - Combined existing and new */}
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--spacing-4)',
          }}>
            <h3 style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 600,
              color: 'var(--color-label)',
              margin: 0,
            }}>
              1. Select Assets for Campaign
            </h3>
            {selectedPreset && (
              <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              <button
                  onClick={handleRefreshAssets}
                  disabled={loadingExisting || syncingMeta || reimportingMeta || uploading}
                  title="Refresh the asset list from database (no API calls to Meta)"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-2)',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-systemBackground)',
                    color: (loadingExisting || syncingMeta || reimportingMeta || uploading) ? 'var(--color-tertiaryLabel)' : 'var(--color-secondaryLabel)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 500,
                    cursor: (loadingExisting || syncingMeta || reimportingMeta || uploading) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                    if (!loadingExisting && !syncingMeta && !reimportingMeta && !uploading) {
                    e.currentTarget.style.background = 'var(--color-secondarySystemFill)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--color-systemBackground)';
                }}
              >
                <svg 
                  style={{ 
                    width: 14, 
                    height: 14,
                    transform: loadingExisting ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.5s ease'
                  }} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                  {loadingExisting ? 'Loading...' : 'Refresh'}
              </button>
                
                <button
                  onClick={handleUploadClick}
                  disabled={loadingExisting || syncingMeta || reimportingMeta || uploading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--color-success)',
                    background: uploading ? 'var(--color-systemGray4)' : 'var(--color-success)',
                    color: uploading ? 'var(--color-tertiaryLabel)' : 'white',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 500,
                    cursor: (loadingExisting || syncingMeta || reimportingMeta || uploading) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!loadingExisting && !syncingMeta && !reimportingMeta && !uploading) {
                      e.currentTarget.style.background = 'var(--color-successDark)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!uploading) {
                      e.currentTarget.style.background = 'var(--color-success)';
                    }
                  }}
                >
                  <svg 
                    style={{ 
                      width: 14, 
                      height: 14,
                      transform: uploading ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.5s ease'
                    }} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  {uploading ? 'Uploading...' : 'Upload Assets'}
                </button>
                
                <button
                  onClick={syncMetaAssets}
                  disabled={loadingExisting || syncingMeta || reimportingMeta || uploading}
                  title="Import new assets from Meta that don't exist in database yet"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--color-primary)',
                    background: syncingMeta ? 'var(--color-systemGray4)' : 'var(--color-primary)',
                    color: syncingMeta ? 'var(--color-tertiaryLabel)' : 'white',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 500,
                    cursor: (loadingExisting || syncingMeta || reimportingMeta || uploading) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!loadingExisting && !syncingMeta && !reimportingMeta && !uploading) {
                      e.currentTarget.style.background = 'var(--color-primary-hover)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!syncingMeta) {
                      e.currentTarget.style.background = 'var(--color-primary)';
                    }
                  }}
                >
                  <svg 
                    style={{ 
                      width: 14, 
                      height: 14,
                      transform: syncingMeta ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.5s ease'
                    }} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {syncingMeta ? 'Syncing Meta...' : 'Sync from Meta'}
                </button>
                
                <button
                  onClick={refreshMetaAssets}
                  disabled={loadingExisting || reimportingMeta || uploading}
                  title="Delete all existing Meta assets and re-import them with fresh URL signatures (fixes expired video URLs)"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--color-warning)',
                    background: reimportingMeta ? 'var(--color-systemGray4)' : 'var(--color-warning)',
                    color: reimportingMeta ? 'var(--color-tertiaryLabel)' : 'white',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 500,
                    cursor: (loadingExisting || reimportingMeta || uploading) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!loadingExisting && !reimportingMeta && !uploading) {
                      e.currentTarget.style.background = 'var(--color-warningDark)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!reimportingMeta) {
                      e.currentTarget.style.background = 'var(--color-warning)';
                    }
                  }}
                >
                  <svg 
                    style={{ 
                      width: 14, 
                      height: 14,
                      transform: reimportingMeta ? 'rotate(360deg)' : 'rotate(0deg)',
                      transition: 'transform 0.8s ease'
                    }} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  {reimportingMeta ? 'Re-importing...' : 'Fix Expired URLs'}
                </button>
                
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </div>
            )}
          </div>
          
          {/* Filter and Sort Controls */}
          {selectedPreset && !loadingExisting && !syncingMeta && !reimportingMeta && existingAssets.length > 0 && (
            <div style={{
              display: 'flex',
              gap: 'var(--spacing-4)',
              marginBottom: 'var(--spacing-4)',
              padding: '0 var(--spacing-4)',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}>
              {/* Filter Controls */}
              <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
                <span style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-secondaryLabel)',
                }}>
                  Filter:
                </span>
              {(['all', 'image', 'video', 'selected'] as const).map((filter) => (
                <button
                  key={filter}
                  onClick={() => handleFilterChange(filter)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: `1px solid ${assetFilter === filter ? 'var(--color-primary)' : 'var(--color-border)'}`,
                    background: assetFilter === filter ? 'var(--color-primary)' : 'var(--color-systemBackground)',
                    color: assetFilter === filter ? 'white' : 'var(--color-secondaryLabel)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (assetFilter !== filter) {
                      e.currentTarget.style.background = 'var(--color-secondarySystemFill)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (assetFilter !== filter) {
                      e.currentTarget.style.background = 'var(--color-systemBackground)';
                    }
                  }}
                >
                  {filter === 'all' ? '📁 All' : 
                   filter === 'image' ? '🖼️ Images' : 
                   filter === 'video' ? '🎥 Videos' : 
                   '✅ Selected'}
                  {filter === 'selected' ? (
                    <span style={{ marginLeft: '4px', fontSize: '11px', opacity: 0.8 }}>
                      ({selectedExistingAssets.length})
                    </span>
                  ) : filter === 'image' ? (
                    <span style={{ marginLeft: '4px', fontSize: '11px', opacity: 0.8 }}>
                      ({imageCount})
                    </span>
                  ) : filter === 'video' ? (
                    <span style={{ marginLeft: '4px', fontSize: '11px', opacity: 0.8 }}>
                      ({videoCount})
                    </span>
                  ) : null}
                </button>
              ))}
              </div>
              
              {/* Sort Controls */}
              <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
                <span style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-secondaryLabel)',
                }}>
                  Sort:
                </span>
                
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'created_at' | 'title' | 'type' | 'file_size')}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-systemBackground)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-label)',
                  }}
                >
                  <option value="created_at">Date</option>
                  <option value="title">Title</option>
                  <option value="type">Type</option>
                  <option value="file_size">Size</option>
                </select>
                
                <button
                  onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                  title={`Currently: ${sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}`}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-systemBackground)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-label)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {sortOrder === 'desc' ? '↓' : '↑'}
                  {sortOrder === 'desc' ? 'Desc' : 'Asc'}
                </button>
                
                {/* Quick sort buttons */}
                <button
                  onClick={() => {
                    setSortBy('type')
                    setSortOrder('asc')
                  }}
                  title="Show images first, then videos"
                  style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    border: '1px solid var(--color-primary)',
                    background: sortBy === 'type' && sortOrder === 'asc' ? 'var(--color-primary)' : 'var(--color-systemBackground)',
                    color: sortBy === 'type' && sortOrder === 'asc' ? 'white' : 'var(--color-primary)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: 'pointer',
                    fontWeight: 500,
                  }}
                >
                  🖼️ Images First
                </button>
              </div>
            </div>
          )}
          
          {!selectedPreset ? (
            <div style={{
              background: 'var(--color-systemBackground)',
              borderRadius: '16px',
              border: '1px solid var(--color-border)',
              padding: 'var(--spacing-8)',
              textAlign: 'center',
              color: 'var(--color-secondaryLabel)',
            }}>
              <div style={{ fontSize: '48px', marginBottom: 'var(--spacing-2)' }}>🔒</div>
              <p style={{ marginBottom: 'var(--spacing-4)' }}>
                Select a client preset to start creating ad campaigns.
              </p>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-tertiaryLabel)' }}>
                Client presets contain your Meta account settings and campaign defaults.
              </p>
            </div>
          ) : (loadingExisting || syncingMeta || reimportingMeta) ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'var(--spacing-8)',
              background: 'var(--color-systemBackground)',
              borderRadius: '16px',
              border: '1px solid var(--color-border)',
            }}>
              <div style={{
                width: 24,
                height: 24,
                border: '3px solid var(--color-tertiaryLabel)',
                borderTop: '3px solid var(--color-primary)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginRight: 'var(--spacing-2)',
              }}></div>
              <span>
                {loadingExisting ? 'Loading your assets...' : reimportingMeta ? 'Re-importing assets from Meta...' : 'Syncing Meta assets...'}
                {(syncingMeta || reimportingMeta) && !loadingExisting && (
                  <span style={{ color: 'var(--color-tertiaryLabel)', fontSize: 'var(--font-size-sm)', marginLeft: 'var(--spacing-2)' }}>
                    (This may take a moment)
                  </span>
                )}
              </span>
            </div>
          ) : (
            <div style={{
              background: 'var(--color-systemBackground)',
              borderRadius: '16px',
              border: '1px solid var(--color-border)',
              padding: 'var(--spacing-4)',
              minHeight: '500px', // Increased height for better viewing
            }}>
              {/* Combined Assets Grid - Existing + New uploads */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', // Increased from 180px to 270px (1.5x)
                gap: 'var(--spacing-4)', // Increased gap for better spacing
                marginBottom: existingAssets.length > 0 || assets.length > 0 ? 'var(--spacing-4)' : 0,
              }}>
                {/* Existing Assets - Use paginated results */}
                {(() => {
                  const paginationInfo = getPaginationInfo()
                  return paginationInfo.currentAssets.map((asset) => (
                  <div
                      key={`existing-${asset.id}`}
                    onClick={() => toggleExistingAsset(asset.id)}
                    style={{
                      position: 'relative',
                      background: 'var(--color-card)',
                      borderRadius: '12px',
                      border: `2px solid ${selectedExistingAssets.includes(asset.id) ? 'var(--color-primary)' : 'var(--color-border)'}`,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      transform: selectedExistingAssets.includes(asset.id) ? 'scale(1.02)' : 'scale(1)',
                    }}
                  >
                    {/* Circle Selection Indicator - Always visible */}
                    <div 
                      style={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        zIndex: 2,
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        border: '2px solid var(--color-primary)',
                        background: selectedExistingAssets.includes(asset.id) ? 'var(--color-primary)' : 'var(--color-systemBackgroundTranslucent)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        backdropFilter: 'blur(4px)',
                        transition: 'all 0.2s ease',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log(`Circle clicked for asset ${asset.id}, current state:`, selectedExistingAssets.includes(asset.id));
                        toggleExistingAsset(asset.id);
                      }}
                    >
                      {selectedExistingAssets.includes(asset.id) && (
                        <span style={{
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: 'bold',
                        }}>
                          ✓
                        </span>
                      )}
                    </div>
                    
                    <div
                      style={{
                        width: '100%',
                          height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        position: 'relative',
                        background: 'var(--color-systemFill)',
                      }}
                    >
                      {asset.thumbnail_url || asset.url ? (
                        <>
                          {asset.type === 'video' ? (
                              // For videos: if we have a URL, show video player; if only thumbnail, show thumbnail with play icon
                              asset.url ? (
                                <VideoAsset 
                              src={asset.url}
                              assetId={asset.id}
                              poster={asset.thumbnail_url}
                                  showPlayButton={true}
                                  autoPlayOnHover={true}
                                  style={{
                                    borderRadius: '8px',
                                    width: '100%',
                                    height: '100%',
                                  }}
                                />
                              ) : asset.thumbnail_url ? (
                                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                  <img
                                    src={asset.thumbnail_url}
                                    alt={`Video thumbnail ${asset.id}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                      objectFit: 'contain',
                              }}
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      const fallback = e.currentTarget.parentElement?.nextElementSibling as HTMLElement;
                                      if (fallback) {
                                        fallback.style.display = 'flex';
                                      }
                                    }}
                                  />
                                  {/* Video play icon overlay */}
                                  <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '40px', // Increased icon size
                                    height: '40px',
                                    background: 'var(--color-modalOverlayDark)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '16px', // Increased font size
                                    pointerEvents: 'none',
                                  }}>
                                    ▶
                                  </div>
                                </div>
                              ) : null
                          ) : (
                            <img
                              src={asset.url || asset.thumbnail_url}
                              alt={`Asset ${asset.id}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                  objectFit: 'contain',
                              }}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                if (fallback) {
                                  fallback.style.display = 'flex';
                                }
                              }}
                            />
                          )}
                        </>
                      ) : (
                          // Enhanced fallback for assets without URLs or thumbnails (Meta videos)
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                            gap: '8px',
                          color: 'var(--color-tertiaryLabel)',
                            textAlign: 'center',
                            padding: '12px',
                        }}>
                            <span style={{ fontSize: '36px' }}>
                            {asset.type === 'video' ? '🎥' : '🖼️'}
                          </span>
                            <span style={{ fontSize: '12px', fontWeight: 600 }}>
                            {asset.type.toUpperCase()}
                          </span>
                          {asset.type === 'video' && asset.fbid && (
                            <>
                              <span style={{ fontSize: '10px', color: 'var(--color-secondaryLabel)' }}>
                                Processing...
                              </span>
                              <button
                                onClick={() => refreshVideoStatus(asset.id)}
                                style={{
                                  background: '#007AFF',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '4px 8px',
                                  fontSize: '10px',
                                  cursor: 'pointer',
                                  marginTop: '4px'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#0056CC'}
                                onMouseLeave={(e) => e.currentTarget.style.background = '#007AFF'}
                              >
                                Refresh
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  ))
                })()}

                {/* New Uploaded Assets */}
              {assets.map((asset, index) => (
                <div
                    key={`new-${index}`}
                  style={{
                    position: 'relative',
                      background: 'var(--color-card)',
                    borderRadius: '12px',
                      border: '2px solid var(--color-success)', // Green border for new uploads
                    overflow: 'hidden',
                  }}
                >
                  <button
                    onClick={() => removeAsset(index)}
                    style={{
                      position: 'absolute',
                        top: 6,
                        right: 6,
                        width: 24,
                        height: 24,
                      background: 'var(--color-errorTranslucent)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      cursor: 'pointer',
                        fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                        fontWeight: 'bold',
                    }}
                  >
                    ×
                  </button>
                    
                    {/* "NEW" badge */}
                    <div style={{
                      position: 'absolute',
                      top: 6,
                      left: 6,
                      background: 'var(--color-success)',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: 600,
                      zIndex: 1,
                    }}>
                      NEW
                    </div>
                    
                  <div
                    style={{
                      width: '100%',
                        height: '100%',
                      overflow: 'hidden',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {asset.file.type.startsWith('video/') ? (
                        <VideoAsset 
                          src={asset.preview}
                          style={{
                            borderRadius: '8px',
                            width: '100%',
                            height: '100%',
                          }}
                          showPlayButton={true}
                          autoPlayOnHover={true}
                        />
                    ) : (
                      <img
                        src={asset.preview}
                        alt={asset.file.name}
                        style={{
                          width: '100%',
                          height: '100%',
                            objectFit: 'contain',
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                          if (fallback) {
                            fallback.style.display = 'flex';
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>

              {/* Upload Button */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                paddingTop: 'var(--spacing-4)',
                borderTop: existingAssets.length > 0 || assets.length > 0 ? '1px solid var(--color-border)' : 'none',
              }}>
                <div
                  style={{
                    border: '2px dashed var(--color-border)',
                    borderRadius: '12px',
                    padding: 'var(--spacing-6)',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: 'transparent',
                    transition: 'all 0.2s',
                    width: '100%',
                    maxWidth: '400px',
                  }}
                  onClick={handleUploadClick}
                >
                  <div style={{
                    fontSize: '32px',
                    marginBottom: 'var(--spacing-2)',
                    color: 'var(--color-systemGray)',
                  }}>
                    📁
                  </div>
                  <h4 style={{
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 600,
                    color: 'var(--color-label)',
                    marginBottom: 'var(--spacing-1)',
                  }}>
                    Add More Assets
                  </h4>
                  <p style={{
                    color: 'var(--color-secondaryLabel)',
                    fontSize: 'var(--font-size-sm)',
                    margin: 0,
                  }}>
                    Click to upload • Max 30 files, 4GB each
                  </p>
                </div>
              </div>

              {/* Pagination Controls */}
              {existingAssets.length > 0 && assetFilter !== 'selected' && (() => {
                const paginationInfo = getPaginationInfo()
                const { totalPages, totalItems, startItem, endItem } = paginationInfo
                
                if (totalPages > 1) {
                  return (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 'var(--spacing-4)',
                      padding: 'var(--spacing-3)',
                      background: 'var(--color-systemGroupedBackground)',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border)',
                    }}>
                      <div style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-secondaryLabel)',
                      }}>
                        Showing {startItem}-{endItem} of {totalItems} assets
                      </div>
                      
                      <div style={{ display: 'flex', gap: 'var(--spacing-1)' }}>
                        {/* Previous button */}
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid var(--color-border)',
                            background: currentPage === 1 ? 'var(--color-systemGray5)' : 'var(--color-systemBackground)',
                            color: currentPage === 1 ? 'var(--color-tertiaryLabel)' : 'var(--color-secondaryLabel)',
                            fontSize: 'var(--font-size-sm)',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                          }}
                        >
                          ← Prev
                        </button>
                        
                        {/* Page numbers */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum
                          if (totalPages <= 5) {
                            pageNum = i + 1
                          } else if (currentPage <= 3) {
                            pageNum = i + 1
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i
                          } else {
                            pageNum = currentPage - 2 + i
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: `1px solid ${currentPage === pageNum ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                background: currentPage === pageNum ? 'var(--color-primary)' : 'var(--color-systemBackground)',
                                color: currentPage === pageNum ? 'white' : 'var(--color-secondaryLabel)',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: currentPage === pageNum ? 600 : 400,
                                cursor: 'pointer',
                                minWidth: '32px',
                              }}
                            >
                              {pageNum}
                            </button>
                          )
                        })}
                        
                        {/* Next button */}
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid var(--color-border)',
                            background: currentPage === totalPages ? 'var(--color-systemGray5)' : 'var(--color-systemBackground)',
                            color: currentPage === totalPages ? 'var(--color-tertiaryLabel)' : 'var(--color-secondaryLabel)',
                            fontSize: 'var(--font-size-sm)',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Next →
                        </button>
                      </div>
                    </div>
                  )
                }
                return null
              })()}

              {/* Assets Summary */}
              {(selectedExistingAssets.length > 0 || assets.length > 0) && (
                <div style={{
                  marginTop: 'var(--spacing-4)',
                  padding: 'var(--spacing-3)',
                  background: 'var(--color-primaryTranslucent)',
                  borderRadius: '8px',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-primary)',
                  textAlign: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <span>
                    {selectedExistingAssets.length} existing + {assets.length} new = {selectedExistingAssets.length + assets.length} assets selected
                  </span>
                  {(selectedExistingAssets.length > 0 || assets.length > 0) && (
                    <span style={{ fontWeight: 600 }}>
                      Ready for variants
                    </span>
                  )}
                </div>
              )}

              {/* Empty State */}
              {existingAssets.length === 0 && assets.length === 0 && !loadingExisting && !syncingMeta && !reimportingMeta && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'var(--spacing-8)',
                  textAlign: 'center',
                  color: 'var(--color-secondaryLabel)',
                  minHeight: '300px',
                }}>
                  <div style={{ fontSize: '64px', marginBottom: 'var(--spacing-4)' }}>📁</div>
                  <h4 style={{
                    color: 'var(--color-label)',
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: 600,
                    marginBottom: 'var(--spacing-2)'
                  }}>
                    No Assets Found
                  </h4>
                  <p style={{
                    fontSize: 'var(--font-size-base)',
                    marginBottom: 'var(--spacing-4)',
                    maxWidth: '400px',
                  }}>
                    Upload assets or sync from Meta to build your library and create ad variations.
                  </p>
                  <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                    <button
                      onClick={syncMetaAssets}
                      disabled={syncingMeta}
                      style={{
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'var(--color-primary)',
                        color: 'white',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        cursor: syncingMeta ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {syncingMeta ? 'Syncing...' : 'Sync from Meta'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Titles Section */}
        <div>
          <h3 style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 600,
            color: 'var(--color-label)',
            marginBottom: 'var(--spacing-3)',
          }}>
            2. Add Titles
          </h3>
          
          {titles.map((title, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                gap: 'var(--spacing-2)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              <input
                type="text"
                value={title}
                onChange={(e) => updateTitle(index, e.target.value)}
                placeholder={`Title ${index + 1}`}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-systemBackground)',
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--color-label)',
                }}
              />
              {titles.length > 1 && (
                <button
                  onClick={() => removeTitle(index)}
                  style={{
                    padding: '12px',
                    borderRadius: '12px',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-systemBackground)',
                    color: 'var(--color-secondaryLabel)',
                    cursor: 'pointer',
                  }}
                >
                  🗑️
                </button>
              )}
            </div>
          ))}
          
          <button
            onClick={addTitle}
            style={{
              padding: '8px 16px',
              borderRadius: '12px',
              border: '1px dashed var(--color-border)',
              background: 'transparent',
              color: 'var(--color-secondaryLabel)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            + Add Another Title
          </button>

          {/* Stored Titles - positioned directly under titles */}
          <div style={{ marginTop: 'var(--spacing-4)' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'var(--spacing-2)',
            }}>
              <h4 style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 600,
                color: 'var(--color-label)',
                margin: 0,
              }}>
                Stored Titles ({storedTitles.filter(title => 
                  contentFilter === 'all' ? true :
                  contentFilter === 'favorites' ? title.is_favorite :
                  selectedStoredTitles.includes(title.id)
                ).length})
              </h4>
              
              <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                {/* Content Filter */}
                <select
                  value={contentFilter}
                  onChange={(e) => handleContentFilterChange(e.target.value as 'all' | 'favorites' | 'selected')}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-systemBackground)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-label)',
                  }}
                >
                  <option value="all">All</option>
                  <option value="favorites">Favorites</option>
                  <option value="selected">Selected</option>
                </select>
                
                {/* Save Current Titles Button */}
                <button
                  onClick={saveCurrentContent}
                  disabled={titles.filter(t => t.trim()).length === 0}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-systemBackground)',
                    color: 'var(--color-label)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: 'pointer',
                    opacity: (titles.filter(t => t.trim()).length === 0) ? 0.5 : 1,
                  }}
                >
                  💾 Save Current
                </button>
                
                {selectedStoredTitles.length > 0 && (
                  <button
                    onClick={addSelectedTitlesToCurrent}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      background: 'var(--color-primary)',
                      color: 'white',
                      border: 'none',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Use {selectedStoredTitles.length} Selected
                  </button>
                )}
              </div>
            </div>
            
            {loadingStoredContent ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 'var(--spacing-4)',
                color: 'var(--color-secondaryLabel)',
                fontSize: 'var(--font-size-sm)',
              }}>
                Loading stored content...
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: 'var(--spacing-2)',
                maxHeight: '200px',
                overflowY: 'auto',
                background: 'var(--color-systemGroupedBackground)',
                borderRadius: '8px',
                padding: 'var(--spacing-2)',
                border: '1px solid var(--color-border)',
              }}>
                {storedTitles
                  .filter(title => 
                    contentFilter === 'all' ? true :
                    contentFilter === 'favorites' ? title.is_favorite :
                    selectedStoredTitles.includes(title.id)
                  )
                  .map((title) => (
                  <div
                    key={title.id}
                    onClick={() => toggleStoredTitle(title.id)}
                    style={{
                      padding: 'var(--spacing-2)',
                      borderRadius: '6px',
                      border: `1px solid ${selectedStoredTitles.includes(title.id) ? 'var(--color-primary)' : 'var(--color-border)'}`,
                      background: selectedStoredTitles.includes(title.id) ? 'var(--color-primaryTranslucent)' : 'var(--color-systemBackground)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative',
                    }}
                  >
                    <div style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-label)',
                      fontWeight: 500,
                      lineHeight: 1.3,
                      marginBottom: '4px',
                    }}>
                      {title.text}
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-1)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-tertiaryLabel)',
                    }}>
                      {title.is_favorite && <span>⭐</span>}
                      {title.usage_count && title.usage_count > 0 && <span>👁️ {title.usage_count}</span>}
                      {title.approach && <span>📝 {title.approach}</span>}
                    </div>
                  </div>
                ))}
                
                {storedTitles.filter(title => 
                  contentFilter === 'all' ? true :
                  contentFilter === 'favorites' ? title.is_favorite :
                  selectedStoredTitles.includes(title.id)
                ).length === 0 && (
                  <div style={{
                    gridColumn: '1 / -1',
                    textAlign: 'center',
                    padding: 'var(--spacing-4)',
                    color: 'var(--color-tertiaryLabel)',
                    fontSize: 'var(--font-size-sm)',
                  }}>
                    {contentFilter === 'favorites' ? 'No favorite titles yet' :
                     contentFilter === 'selected' ? 'No titles selected' :
                     'No stored titles yet. Save some content to reuse later!'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Headlines Section */}
        <div>
          <h3 style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 600,
            color: 'var(--color-label)',
            marginBottom: 'var(--spacing-3)',
          }}>
            3. Add Headlines
          </h3>
          
          {headlines.map((headline, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                gap: 'var(--spacing-2)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              <textarea
                value={headline}
                onChange={(e) => updateHeadline(index, e.target.value)}
                placeholder={`Headline ${index + 1}`}
                rows={3}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-systemBackground)',
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--color-label)',
                  resize: 'vertical',
                  minHeight: '80px',
                }}
              />
              {headlines.length > 1 && (
                <button
                  onClick={() => removeHeadline(index)}
                  style={{
                    padding: '12px',
                    borderRadius: '12px',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-systemBackground)',
                    color: 'var(--color-secondaryLabel)',
                    cursor: 'pointer',
                    alignSelf: 'flex-start',
                  }}
                >
                  🗑️
                </button>
              )}
            </div>
          ))}
          
          <button
            onClick={addHeadline}
            style={{
              padding: '8px 16px',
              borderRadius: '12px',
              border: '1px dashed var(--color-border)',
              background: 'transparent',
              color: 'var(--color-secondaryLabel)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            + Add Another Headline
          </button>

          {/* Stored Headlines - positioned directly under headlines */}
          <div style={{ marginTop: 'var(--spacing-4)' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'var(--spacing-2)',
            }}>
              <h4 style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 600,
                color: 'var(--color-label)',
                margin: 0,
              }}>
                Stored Headlines ({storedHeadlines.filter(headline => 
                  contentFilter === 'all' ? true :
                  contentFilter === 'favorites' ? headline.is_favorite :
                  selectedStoredHeadlines.includes(headline.id)
                ).length})
              </h4>
              
              <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                {/* Save Current Headlines Button */}
                <button
                  onClick={saveCurrentContent}
                  disabled={headlines.filter(h => h.trim()).length === 0}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-systemBackground)',
                    color: 'var(--color-label)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: 'pointer',
                    opacity: (headlines.filter(h => h.trim()).length === 0) ? 0.5 : 1,
                  }}
                >
                  💾 Save Current
                </button>
                
                {selectedStoredHeadlines.length > 0 && (
                  <button
                    onClick={addSelectedHeadlinesToCurrent}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      background: 'var(--color-primary)',
                      color: 'white',
                      border: 'none',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Use {selectedStoredHeadlines.length} Selected
                  </button>
                )}
              </div>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: 'var(--spacing-2)',
              maxHeight: '200px',
              overflowY: 'auto',
              background: 'var(--color-systemGroupedBackground)',
              borderRadius: '8px',
              padding: 'var(--spacing-2)',
              border: '1px solid var(--color-border)',
            }}>
              {storedHeadlines
                .filter(headline => 
                  contentFilter === 'all' ? true :
                  contentFilter === 'favorites' ? headline.is_favorite :
                  selectedStoredHeadlines.includes(headline.id)
                )
                .map((headline) => (
                <div
                  key={headline.id}
                  onClick={() => toggleStoredHeadline(headline.id)}
                  style={{
                    padding: 'var(--spacing-2)',
                    borderRadius: '6px',
                    border: `1px solid ${selectedStoredHeadlines.includes(headline.id) ? 'var(--color-primary)' : 'var(--color-border)'}`,
                    background: selectedStoredHeadlines.includes(headline.id) ? 'var(--color-primaryTranslucent)' : 'var(--color-systemBackground)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                  }}
                >
                  <div style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-label)',
                    fontWeight: 500,
                    lineHeight: 1.3,
                    marginBottom: '4px',
                  }}>
                    {headline.text}
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-1)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-tertiaryLabel)',
                  }}>
                    {headline.is_favorite && <span>⭐</span>}
                                         {headline.usage_count && headline.usage_count > 0 && <span>👁️ {headline.usage_count}</span>}
                    {headline.style && <span>🎨 {headline.style}</span>}
                    {headline.length && <span>📏 {headline.length}</span>}
                  </div>
                </div>
              ))}
              
              {storedHeadlines.filter(headline => 
                contentFilter === 'all' ? true :
                contentFilter === 'favorites' ? headline.is_favorite :
                selectedStoredHeadlines.includes(headline.id)
              ).length === 0 && (
                <div style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: 'var(--spacing-4)',
                  color: 'var(--color-tertiaryLabel)',
                  fontSize: 'var(--font-size-sm)',
                }}>
                  {contentFilter === 'favorites' ? 'No favorite headlines yet' :
                   contentFilter === 'selected' ? 'No headlines selected' :
                   'No stored headlines yet. Save some content to reuse later!'}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Summary */}
        {(selectedExistingAssets.length > 0 || assets.length > 0) && (
          <div style={{
            background: 'var(--color-systemGroupedBackground)',
            borderRadius: '12px',
            padding: 'var(--spacing-4)',
            border: '1px solid var(--color-border)',
          }}>
            <h4 style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 600,
              color: 'var(--color-label)',
              marginBottom: 'var(--spacing-2)',
            }}>
              Summary
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 'var(--spacing-2)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-secondaryLabel)',
            }}>
              <div>📁 {selectedExistingAssets.length} existing assets</div>
              <div>📤 {assets.length} new uploads</div>
              <div>📝 {titles.filter(t => t.trim()).length} titles</div>
              <div>📄 {headlines.filter(h => h.trim()).length} headlines</div>
              <div style={{ fontWeight: 600, color: 'var(--color-label)' }}>
                = {calculateTotalCombinations()} total variants
              </div>
            </div>
          </div>
        )}

        {/* Progress */}
        {(uploading || generating) && (
          <div style={{
            background: 'var(--color-systemGroupedBackground)',
            borderRadius: '12px',
            padding: 'var(--spacing-4)',
            border: '1px solid var(--color-border)',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-3)',
              marginBottom: 'var(--spacing-3)',
            }}>
              <div style={{
                width: 24,
                height: 24,
                border: '3px solid var(--color-tertiaryLabel)',
                borderTop: '3px solid var(--color-primary)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}></div>
              <span style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 600,
                color: 'var(--color-label)',
              }}>
                {uploading ? 'Uploading Assets...' : 'Generating Variants...'}
              </span>
            </div>
            
            {uploading && (
              <div style={{
                width: '100%',
                height: 8,
                background: 'var(--color-systemGray5)',
                borderRadius: 4,
                overflow: 'hidden',
              }}>
                <div style={{
                  width: `${uploadProgress}%`,
                  height: '100%',
                  background: 'var(--color-primary)',
                  transition: 'width 0.3s',
                }}></div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{
          display: 'flex',
          gap: 'var(--spacing-3)',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <button
            onClick={testConnectivity}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid var(--color-border)',
              background: 'var(--color-systemBackground)',
              color: 'var(--color-secondaryLabel)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
            }}
          >
            Test API Connection
          </button>
          
          <button
            onClick={handleGenerateVariants}
            disabled={uploading || generating || (selectedExistingAssets.length === 0 && assets.length === 0)}
            style={{
              background: (uploading || generating || (selectedExistingAssets.length === 0 && assets.length === 0)) 
                ? 'var(--color-systemGray4)' 
                : 'var(--color-primary)',
              color: (uploading || generating || (selectedExistingAssets.length === 0 && assets.length === 0)) 
                ? 'var(--color-tertiaryLabel)' 
                : 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '16px 32px',
              fontSize: 'var(--font-size-base)',
              fontWeight: 600,
              cursor: (uploading || generating || (selectedExistingAssets.length === 0 && assets.length === 0)) 
                ? 'not-allowed' 
                : 'pointer',
              transition: 'all 0.2s',
            }}
          >
            {uploading ? 'Uploading...' : generating ? 'Generating...' : 'Generate Ad Variants'}
          </button>
        </div>
      </div>

      {/* Upload Progress Bar */}
      {uploading && uploadProgress > 0 && (
        <div style={{
          background: 'var(--color-systemGroupedBackground)',
          borderRadius: '8px',
          padding: 'var(--spacing-4)',
          margin: 'var(--spacing-4) 0',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-3)',
            marginBottom: 'var(--spacing-2)',
          }}>
            <div style={{
              width: 20,
              height: 20,
              border: '2px solid var(--color-tertiaryLabel)',
              borderTop: '2px solid var(--color-success)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}></div>
            <span style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 600,
              color: 'var(--color-label)',
            }}>
              Uploading Assets... {uploadProgress}%
            </span>
          </div>
          
          <div style={{
            width: '100%',
            height: 8,
            background: 'var(--color-systemGray5)',
            borderRadius: 4,
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${uploadProgress}%`,
              height: '100%',
              background: 'var(--color-success)',
              transition: 'width 0.3s',
            }}></div>
          </div>
        </div>
      )}

      {/* Marti AI Assistant */}
      <AICreativeAssistant 
        onContentGenerated={handleContentGenerated}
        onImageGenerated={handleImageGenerated}
        clientId={getClientId()}
      />
    </div>
  )
} 